import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { rpc } from '@/lib/api';

export const slotSchema = z.object({ slot_start: z.string(), local_date: z.string(), local_time: z.string(), available: z.boolean() });
export type Slot = z.infer<typeof slotSchema>;

export function availabilityQuery(slug: string, serviceIds: string[], day: string) {
  const ids = [...serviceIds].sort();
  return queryOptions({
    queryKey: ['availability', slug, ids, day],
    queryFn: async () => z.array(slotSchema).parse(await rpc('get_availability', { p_slug: slug, p_service_ids: ids, p_from: day, p_days: 1 })),
    enabled: ids.length > 0,
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  });
}

export const bookingViewSchema = z.object({
  id: z.uuid(),
  public_code: z.string(),
  status: z.enum(['confirmed', 'checked_in', 'ready', 'cancelled', 'no_show']),
  starts_at: z.string(),
  ends_at: z.string(),
  total_minor: z.number(),
  currency: z.string(),
  vehicle_class: z.string(),
  resource_name: z.string(),
  customer_name: z.string(),
  vehicle: z.object({ make_model: z.string(), plate: z.string().nullable() }),
  is_demo: z.boolean(),
  cancel_until: z.string(),
  can_cancel: z.boolean(),
  tenant: z.object({ slug: z.string(), name: z.string(), address: z.string(), phone: z.string(), timezone: z.string() }),
  items: z.array(z.object({ service_id: z.uuid().nullable(), name: z.string(), category: z.string(), price_minor: z.number(), duration_minutes: z.number() })),
});
export type BookingView = z.infer<typeof bookingViewSchema>;

export const createdBookingSchema = bookingViewSchema.extend({ token: z.string(), replayed: z.boolean() });
export type CreatedBooking = z.infer<typeof createdBookingSchema>;

export function bookingQuery(slug: string, token: string) {
  return queryOptions({
    queryKey: ['booking', slug, token],
    queryFn: async () => bookingViewSchema.parse(await rpc('get_booking_by_token', { p_slug: slug, p_token: token })),
    retry: 1,
  });
}

export interface BookingDraft {
  serviceIds: string[];
  vehicleClassId: string;
  startsAt: string;
  name: string;
  phone: string;
  makeModel: string;
  plate: string;
  note: string;
}

export async function createBooking(slug: string, d: BookingDraft, idempotencyKey: string): Promise<CreatedBooking> {
  return createdBookingSchema.parse(
    await rpc('create_booking', {
      p_slug: slug,
      p_service_ids: d.serviceIds,
      p_vehicle_class_id: d.vehicleClassId,
      p_starts_at: d.startsAt,
      p_customer_name: d.name.trim(),
      p_customer_phone: d.phone,
      p_vehicle_make_model: d.makeModel.trim(),
      p_vehicle_plate: d.plate.trim() || null,
      p_note: d.note.trim() || null,
      p_idempotency_key: idempotencyKey,
    }),
  );
}

export async function cancelBooking(slug: string, token: string): Promise<BookingView> {
  return bookingViewSchema.parse(await rpc('cancel_booking_by_token', { p_slug: slug, p_token: token, p_reason: null }));
}
